import React, { useState, useEffect } from 'react';
import { Modal, Tabs, Form, Input, Button, Select, Upload, message, Avatar, Table, Spin, Typography } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import apiClient from '../services/apiClient';
import { useLanguage } from '../contexts/LanguageContext';
import { GMT_OFFSET_OPTIONS, normalizeGMTOffsetString, getTimezoneValueByGMTOffset } from '../configs/timezones';
import { getUserInterfacesFromStorage, expandInterfacesWithChildren } from '../utils/permissionUtils';

const { Text } = Typography;

const { TabPane } = Tabs;

const MyPreferencesModal = ({ visible, onClose, userInfo, onUserInfoUpdate, showUserAdminFields = false }) => {
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageReport, setUsageReport] = useState(null);
  const [showUsageTab, setShowUsageTab] = useState(false);
  const { changeLanguage, currentLanguage, t } = useLanguage();

  // 同步 userInfo 到表單
  useEffect(() => {
    console.log('userInfo:', userInfo); // debug 印出 userInfo 結構
    if (userInfo && visible && form) {
      form.resetFields();
      form.setFieldsValue({
        account: userInfo.account || userInfo.user_account || '',
        name: userInfo.name || '',
        email: userInfo.email || '',
        phone: userInfo.phone || '',
        language: userInfo.language || 'zh-TC',
        timezone: normalizeGMTOffsetString(userInfo.timezone),
        avatar_url: userInfo.avatar_url || '',
        is_active: typeof userInfo.is_active !== 'undefined' ? userInfo.is_active : (typeof userInfo.isActive !== 'undefined' ? userInfo.isActive : undefined),
        is_owner: typeof userInfo.is_owner !== 'undefined' ? userInfo.is_owner : (typeof userInfo.isOwner !== 'undefined' ? userInfo.isOwner : undefined),
      });
      setAvatarUrl(userInfo.avatar_url || '');
    }
  }, [userInfo, visible, form]);

  useEffect(() => {
    if (visible && form) {
      form.setFieldsValue({ language: currentLanguage });
    }
  }, [currentLanguage, visible, form]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      const interfaces = await getUserInterfacesFromStorage(true);
      const expanded = expandInterfacesWithChildren(interfaces || []);
      const can = expanded.includes('preferences.usageBilling');
      if (!cancelled) setShowUsageTab(can);
      if (!can) return;
      setUsageLoading(true);
      try {
        const res = await apiClient.get('/api/usage-billing/my-company/monthly');
        if (!cancelled) setUsageReport(res.data);
      } catch {
        if (!cancelled) setUsageReport(null);
      } finally {
        if (!cancelled) setUsageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible]);

  const handleUpload = async ({ file }) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await apiClient.post('/api/users/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (res.data.success) {
        setAvatarUrl(res.data.url);
        form.setFieldsValue({ avatar_url: res.data.url });
        message.success(t('preferences.avatarUploadSuccess'));
      } else {
        message.error(res.data.message || t('preferences.avatarUploadFailed'));
      }
    } catch (e) {
      message.error(t('preferences.avatarUploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const normalizedTimezone = normalizeGMTOffsetString(values.timezone);
      const timezoneForSave = getTimezoneValueByGMTOffset(normalizedTimezone);
      
      // 構建與 AuthController UpdateMe 端點匹配的 payload
      const payload = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        language: values.language,
        timezone: timezoneForSave,
        AvatarUrl: avatarUrl
      };
      
      // 如果密碼有填寫，則添加到 payload
      if (values.password_hash) {
        payload.PasswordHash = values.password_hash;
      }
      
      // 使用 /api/auth/me 端點更新用戶信息
      await apiClient.put('/api/auth/me', payload);
      
      // 重新取得最新 userInfo
      const res = await apiClient.get('/api/auth/me');
      
      if (res.data) {
        if (typeof onUserInfoUpdate === 'function') {
          onUserInfoUpdate(res.data);
          // 主動刷新表單欄位
          form.setFieldsValue({
            name: res.data.name || '',
            email: res.data.email || '',
            phone: res.data.phone || '',
            language: res.data.language || 'zh-TC',
            timezone: normalizeGMTOffsetString(res.data.timezone),
            avatar_url: res.data.avatar_url || ''
          });
        }
        localStorage.setItem('userInfo', JSON.stringify(res.data));
      }
      // 立即切換語言
      changeLanguage(values.language);
      setTimeout(() => {
        message.success(t('preferences.updateSuccess'));
        onClose();
      }, 100);
    } catch (e) {
      // 驗證失敗或 API 失敗
      console.error('更新用戶信息失敗:', e);
      message.error(t('preferences.updateFailed'));
    }
  };

  return (
    <Modal
      title={t('preferences.title')}
      open={visible}
      onCancel={onClose}
      onOk={handleOk}
      okText={t('preferences.save')}
      cancelText={t('preferences.cancel')}
      destroyOnHidden={false}
      width="50%"
    >
      <Tabs defaultActiveKey="1">
        <TabPane tab={t('preferences.basicInfo')} key="1">
          <Form form={form} layout="vertical">
            <Form.Item label={t('preferences.avatar')} name="avatar_url">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 16 }}>
                <Upload
                  name="file"
                  showUploadList={false}
                  customRequest={handleUpload}
                  accept="image/*"
                  disabled={uploading}
                >
                  {avatarUrl ? (
                    <Avatar src={avatarUrl} size={96} style={{ marginBottom: 8 }} />
                  ) : (
                    <Button icon={<UploadOutlined />}>{t('preferences.uploadAvatar')}</Button>
                  )}
                </Upload>
              </div>
            </Form.Item>
            <Form.Item label={t('preferences.account')} name="account">
              <Input disabled />
            </Form.Item>
            <Form.Item label={t('preferences.name')} name="name" rules={[{ required: true, message: t('preferences.name') + ' ' + t('common.required') }]}>
              <Input />
            </Form.Item>
            <Form.Item label={t('preferences.email')} name="email" rules={[{ required: true, message: t('preferences.email') + ' ' + t('common.required') }]}>
              <Input />
            </Form.Item>
            <Form.Item label={t('preferences.phone')} name="phone">
              <Input />
            </Form.Item>
            {showUserAdminFields && (
              <Form.Item label={t('preferences.isActive')} name="is_active" valuePropName="checked">
                <Input type="checkbox" style={{ width: 20, height: 20 }} />
              </Form.Item>
            )}
            {showUserAdminFields && (
              <Form.Item label={t('preferences.isOwner')} name="is_owner" valuePropName="checked">
                <Input type="checkbox" style={{ width: 20, height: 20 }} />
              </Form.Item>
            )}
            <Form.Item label={t('preferences.language')} name="language">
              <Select
                options={[
                  { value: 'zh-TC', label: '繁體中文' },
                  { value: 'zh-SC', label: '简体中文' },
                  { value: 'en', label: 'English' }
                ]}
              />
            </Form.Item>
            <Form.Item label={t('preferences.timezone')} name="timezone">
              <Select
                showSearch
                placeholder={t('preferences.selectTimezone')}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={GMT_OFFSET_OPTIONS}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label={t('preferences.password')} name="password_hash" rules={[{ min: 6, message: t('preferences.password') + ' ' + t('common.minLength', { min: 6 }) }]}>
              <Input.Password autoComplete="new-password" placeholder={t('preferences.passwordPlaceholder')} />
            </Form.Item>
          </Form>
        </TabPane>
        {showUsageTab && (
          <TabPane tab={t('preferences.usageBillingTab') || t('usageBilling.title') || '用量計費'} key="2">
            {usageLoading ? (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin />
              </div>
            ) : usageReport ? (
              <div>
                <p>
                  <Text type="secondary">
                    {t('usageBilling.joinDate') || '計費週期起點'}:{' '}
                    {usageReport.joinDateUtc
                      ? new Date(usageReport.joinDateUtc).toLocaleString()
                      : '—'}
                  </Text>
                </p>
                <p>
                  <Text type="secondary">
                    {t('usageBilling.rates') || '單價'} ({usageReport.currency || 'HKD'}):{' '}
                    {t('usageBilling.metaOutbound') || 'Meta'} {usageReport.rates?.metaTemplateOutbound ?? '—'} /{' '}
                    {t('usageBilling.aiCall') || 'AI'} {usageReport.rates?.aiCall ?? '—'} /{' '}
                    {t('usageBilling.workflowExecution') || '執行'} {usageReport.rates?.workflowExecution ?? '—'}
                  </Text>
                </p>
                <Table
                  size="small"
                  rowKey={(m) => String(m.monthIndex)}
                  pagination={false}
                  dataSource={usageReport.months || []}
                  columns={[
                    {
                      title: t('usageBilling.period') || '計費期間',
                      render: (_, m) => {
                        const s = new Date(m.periodStartUtc);
                        const e = new Date(m.periodEndUtcExclusive);
                        return `${s.toLocaleDateString()} – ${e.toLocaleDateString()}`;
                      },
                    },
                    {
                      title: '#',
                      dataIndex: 'monthIndex',
                      width: 48,
                    },
                    {
                      title: t('usageBilling.workflowExecution') || '流程執行',
                      render: (_, m) => m.counts?.workflowExecution ?? 0,
                    },
                    {
                      title: t('usageBilling.metaOutbound') || 'Meta 對外',
                      render: (_, m) => m.counts?.metaOutbound ?? 0,
                    },
                    {
                      title: t('usageBilling.aiCall') || 'AI',
                      render: (_, m) => m.counts?.aiCall ?? 0,
                    },
                    {
                      title: t('usageBilling.totalAmount') || '小計',
                      render: (_, m) => {
                        const cur = usageReport.currency || 'HKD';
                        const v = m.amounts?.total != null ? Number(m.amounts.total) : 0;
                        return `${cur} ${v.toFixed(2)}`;
                      },
                    },
                  ]}
                />
              </div>
            ) : (
              <Text type="danger">{t('usageBilling.loadFailed') || '無法載入用量'}</Text>
            )}
          </TabPane>
        )}
      </Tabs>
    </Modal>
  );
};

export default MyPreferencesModal;